import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Blinds,
  Sun,
  Power,
  Settings,
  ChevronUp,
  ChevronDown,
  TrendingDown,
  Zap,
  Clock,
  Loader2,
  Square,
} from 'lucide-react';
import { ultronTuringApi } from '../../services/ultronTuringApi';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
} from 'recharts';

interface DeviceZone {
  id: string;
  name: string;
  devices: string[];
}

interface Device {
  deviceId: string;
  baseSN: string;
  deviceType: string;
  displayName?: string;
  name?: string;
  isOn?: boolean;
  openPercent?: number;
  traits?: string[];
  attributes?: any;
}

interface BlindCurtainControlViewProps {
  devices: Device[];
  zones: DeviceZone[];
  onOpenZoneSelector?: () => void;
  selectedZoneCount?: number;
}

// Demo zone data for blinds/curtains
const initialDemoZones = [
  { id: 'demo-1', name: 'Floor 1 - Lobby East', position: 30, mode: 'solar' as const, type: 'blind' as const, isAuto: true },
  { id: 'demo-2', name: 'Floor 1 - Lobby West', position: 70, mode: 'solar' as const, type: 'curtain' as const, isAuto: true },
  { id: 'demo-3', name: 'Floor 1 - Conference A', position: 0, mode: 'manual' as const, type: 'blind' as const, isAuto: false },
  { id: 'demo-4', name: 'Floor 2 - Open Office N', position: 45, mode: 'occupancy' as const, type: 'blind' as const, isAuto: true },
  { id: 'demo-5', name: 'Floor 2 - Open Office S', position: 25, mode: 'solar' as const, type: 'blind' as const, isAuto: true },
  { id: 'demo-6', name: 'Floor 3 - Executive Suite', position: 60, mode: 'scheduled' as const, type: 'curtain' as const, isAuto: true },
  { id: 'demo-7', name: 'Floor 2 - Break Room', position: 100, mode: 'manual' as const, type: 'curtain' as const, isAuto: false },
  { id: 'demo-8', name: 'Floor 3 - Server Room', position: 0, mode: 'thermal' as const, type: 'blind' as const, isAuto: false },
];

// Generate solar schedule data
const generateSolarScheduleData = () => {
  const hours = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];
  return hours.map((time, i) => {
    // Automated curve - follows sun position
    const automated = Math.sin((i / 7) * Math.PI) * 50 + 50;
    // Actual - slight variation
    const actual = automated + (Math.random() - 0.5) * 20;
    return {
      time,
      automated: Math.round(Math.max(0, Math.min(100, automated))),
      actual: Math.round(Math.max(0, Math.min(100, actual))),
    };
  });
};

// Generate HVAC load reduction data
const generateHVACLoadData = () => {
  const hours = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'];
  return hours.map((time, i) => {
    const baseLoad = 20;
    const withoutShading = baseLoad + Math.sin((i / 5) * Math.PI) * 80;
    const withShading = baseLoad + Math.sin((i / 5) * Math.PI) * 40;
    return {
      time,
      withoutShading: Math.round(Math.max(0, withoutShading)),
      withShading: Math.round(Math.max(0, withShading)),
      savings: Math.round(withoutShading - withShading),
    };
  });
};

// Get position status text
const getPositionStatus = (position: number): string => {
  if (position === 0) return 'Fully Closed';
  if (position === 100) return 'Fully Open';
  if (position <= 25) return 'Mostly Closed';
  if (position >= 75) return 'Mostly Open';
  return 'Half Open';
};

// Get mode display text
const getModeText = (mode: string): string => {
  switch (mode) {
    case 'solar': return 'Solar Tracking';
    case 'manual': return 'Manual';
    case 'scheduled': return 'Scheduled';
    case 'occupancy': return 'Occupancy Based';
    case 'thermal': return 'Thermal Optimize';
    default: return mode;
  }
};

// Get mode icon
const getModeIcon = (mode: string) => {
  switch (mode) {
    case 'solar': return <Sun className="w-3 h-3" />;
    case 'scheduled': return <Clock className="w-3 h-3" />;
    default: return <Settings className="w-3 h-3" />;
  }
};

const BlindCurtainControlView: React.FC<BlindCurtainControlViewProps> = ({ 
  devices, 
  zones,
  onOpenZoneSelector,
  selectedZoneCount
}) => {
  const [masterPosition, setMasterPosition] = useState(50);
  const [solarTracking, setSolarTracking] = useState(true);
  const [isCommandLoading, setIsCommandLoading] = useState(false);
  const commandDebounce = useRef<NodeJS.Timeout | null>(null);
  
  const [zonePositions, setZonePositions] = useState<Record<string, number>>(() => {
    const positions: Record<string, number> = {};
    initialDemoZones.forEach(z => { positions[z.id] = z.position; });
    return positions;
  });
  
  const [zoneAutoStates, setZoneAutoStates] = useState<Record<string, boolean>>(() => {
    const states: Record<string, boolean> = {};
    initialDemoZones.forEach(z => { states[z.id] = z.isAuto; });
    return states;
  });
  
  const solarScheduleData = useMemo(() => generateSolarScheduleData(), []);
  const hvacLoadData = useMemo(() => generateHVACLoadData(), []);
  
  // Filter blind/curtain devices
  const blindCurtainDevices = useMemo(() =>
    devices.filter(d =>
      d.deviceType === 'blind' ||
      d.deviceType === 'curtain' ||
      d.deviceType === 'shade' ||
      d.deviceType === 'shutter' ||
      d.traits?.some(t => ['OpenClose'].includes(t))
    ), [devices]);
  
  // Get device IDs for commands
  const blindCurtainDeviceIds = useMemo(() => blindCurtainDevices.map(d => d.deviceId), [blindCurtainDevices]);
  
  // Generate zone stats from real zones or demo data
  const zoneStats = useMemo(() => {
    if (zones.length === 0) {
      return initialDemoZones.map(demo => ({
        ...demo,
        position: zonePositions[demo.id] ?? demo.position,
        isAuto: zoneAutoStates[demo.id] ?? demo.isAuto,
      }));
    }
    
    return zones.map((zone, i) => {
      const demo = initialDemoZones[i % initialDemoZones.length];
      return {
        id: zone.id,
        name: zone.name,
        position: zonePositions[zone.id] ?? demo.position,
        mode: demo.mode,
        type: i % 3 === 0 ? 'curtain' as const : 'blind' as const,
        isAuto: zoneAutoStates[zone.id] ?? demo.isAuto,
      };
    });
  }, [zones, zonePositions, zoneAutoStates]);
  
  // Calculate stats
  const blindCount = zoneStats.filter(z => z.type === 'blind').length;
  const curtainCount = zoneStats.filter(z => z.type === 'curtain').length;
  
  // Handle Open All
  const handleOpenAll = useCallback(async () => {
    if (blindCurtainDeviceIds.length === 0) {
      // Demo mode - update all zone positions
      setZonePositions(prev => {
        const newPositions: Record<string, number> = { ...prev };
        zoneStats.forEach(z => { newPositions[z.id] = 100; });
        return newPositions;
      });
      setMasterPosition(100);
      return;
    }
    
    setIsCommandLoading(true);
    try {
      await ultronTuringApi.executeCommand('open', blindCurtainDeviceIds);
      setMasterPosition(100);
    } catch (error) {
      console.error('Failed to open all:', error);
    } finally {
      setIsCommandLoading(false);
    }
  }, [blindCurtainDeviceIds, zoneStats]);
  
  // Handle Close All
  const handleCloseAll = useCallback(async () => {
    if (blindCurtainDeviceIds.length === 0) {
      // Demo mode - update all zone positions
      setZonePositions(prev => {
        const newPositions: Record<string, number> = { ...prev };
        zoneStats.forEach(z => { newPositions[z.id] = 0; });
        return newPositions;
      });
      setMasterPosition(0);
      return;
    }
    
    setIsCommandLoading(true);
    try {
      await ultronTuringApi.executeCommand('close', blindCurtainDeviceIds);
      setMasterPosition(0);
    } catch (error) {
      console.error('Failed to close all:', error);
    } finally {
      setIsCommandLoading(false);
    }
  }, [blindCurtainDeviceIds, zoneStats]);
  
  // Handle Master Position Change
  const handleMasterPositionChange = useCallback(async (newPosition: number) => {
    setMasterPosition(newPosition);
    
    // Update all zone positions in demo mode
    if (blindCurtainDeviceIds.length === 0) {
      setZonePositions(prev => {
        const newPositions: Record<string, number> = { ...prev };
        zoneStats.forEach(z => { newPositions[z.id] = newPosition; });
        return newPositions;
      });
      return;
    }
    
    // Debounce API call
    if (commandDebounce.current) {
      clearTimeout(commandDebounce.current);
    }
    
    commandDebounce.current = setTimeout(async () => {
      setIsCommandLoading(true);
      try {
        await ultronTuringApi.executeCommand('setOpenPercent', blindCurtainDeviceIds, { openPercent: newPosition });
      } catch (error) {
        console.error('Failed to set position:', error);
      } finally {
        setIsCommandLoading(false);
      }
    }, 500);
  }, [blindCurtainDeviceIds, zoneStats]);
  
  // Handle Zone Position Change
  const handleZonePositionChange = useCallback((zoneId: string, newPosition: number) => {
    setZonePositions(prev => ({ ...prev, [zoneId]: newPosition }));
  }, []);
  
  // Handle Zone Auto Toggle
  const handleZoneAutoToggle = useCallback((zoneId: string) => {
    setZoneAutoStates(prev => ({ ...prev, [zoneId]: !prev[zoneId] }));
  }, []);

  return (
    <div className="space-y-4">
      {/* Top Control Cards - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Master Position */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Blinds className="w-4 h-4 text-cyan-400" />
              <span className="text-base font-semibold text-white">Master Position</span>
            </div>
            <span className="text-3xl font-bold text-white">{masterPosition}%</span>
          </div>
          
          <div className="space-y-2">
            <input
              type="range"
              min="0"
              max="100"
              value={masterPosition}
              onChange={(e) => handleMasterPositionChange(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${masterPosition}%, #334155 ${masterPosition}%, #334155 100%)`
              }}
            />
            <div className="flex justify-between text-sm text-slate-500">
              <span>Closed</span>
              <span>Half Open</span>
              <span>Open</span>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Power className="w-4 h-4 text-cyan-400" />
            <span className="text-base font-semibold text-white">Quick Actions</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleOpenAll}
              disabled={isCommandLoading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <ChevronUp className="w-4 h-4 text-slate-300" />
              <span className="text-base text-slate-300">Open All</span>
            </button>
            <button
              onClick={handleCloseAll}
              disabled={isCommandLoading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <ChevronDown className="w-4 h-4 text-slate-300" />
              <span className="text-base text-slate-300">Close All</span>
            </button>
          </div>
        </div>
        
        {/* Solar Tracking */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-amber-400" />
              <span className="text-base font-semibold text-white">Solar Tracking</span>
            </div>
            <button
              onClick={() => setSolarTracking(!solarTracking)}
              className={`relative w-12 h-6 rounded-full transition-colors ${solarTracking ? 'bg-cyan-500' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${solarTracking ? 'left-7' : 'left-1'}`} />
            </button>
          </div>
          
          <p className="text-base text-slate-400 mb-3">
            Automatically adjusts blinds based on sun position and glare detection
          </p>
          
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-sm font-medium ${solarTracking ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
              {solarTracking ? 'Active' : 'Inactive'}
            </span>
            {solarTracking && (
              <span className="text-xs text-green-400">Reducing HVAC load by 18%</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Charts Row - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Solar Schedule Chart */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Sun className="w-4 h-4 text-amber-400" />
            <span className="text-base font-semibold text-white">Solar Schedule</span>
          </div>
          <p className="text-base text-slate-400 mb-4">Today's automated shading curve</p>
          
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={solarScheduleData}>
                <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0d1424', border: '1px solid #1e293b', borderRadius: '8px', fontSize: '14px' }}
                  labelStyle={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}
                  itemStyle={{ color: '#94a3b8', fontSize: '13px' }}
                />
                <Line
                  type="monotone"
                  dataKey="automated"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Automated"
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#06b6d4"
                  strokeWidth={2}
                  dot={false}
                  name="Actual"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* HVAC Load Reduction Chart */}
        <div className="bg-[#0C2340] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-green-400" />
            <span className="text-base font-semibold text-white">HVAC Load Reduction</span>
          </div>
          <p className="text-base text-slate-400 mb-4">Impact of automated shading on cooling load</p>
          
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hvacLoadData}>
                <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0d1424', border: '1px solid #1e293b', borderRadius: '8px', fontSize: '14px' }}
                  labelStyle={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}
                  itemStyle={{ color: '#94a3b8', fontSize: '13px' }}
                />
                <Area
                  type="monotone"
                  dataKey="withoutShading"
                  stroke="#ef4444"
                  fill="#ef4444"
                  fillOpacity={0.3}
                  name="Without Shading"
                />
                <Area
                  type="monotone"
                  dataKey="withShading"
                  stroke="#22c55e"
                  fill="#22c55e"
                  fillOpacity={0.5}
                  name="With Shading"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Zone Controls Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-cyan-400" />
            <span className="text-xl font-semibold text-white">Zone Controls</span>
            <span className="text-base text-slate-400 ml-2">Individual blind and curtain management</span>
          </div>
          <div className="flex items-center gap-4 text-base text-slate-400">
            <span className="flex items-center gap-1">
              <Blinds className="w-4 h-4" />
              {blindCount} Blinds
            </span>
            <span className="flex items-center gap-1">
              <Square className="w-4 h-4" />
              {curtainCount} Curtains
            </span>
            {onOpenZoneSelector && (
              <button
                onClick={onOpenZoneSelector}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
                title="選擇區域"
              >
                <Settings className="w-3 h-3" />
                <span>區域</span>
                {typeof selectedZoneCount === 'number' && (
                  <span className="text-slate-500">({selectedZoneCount})</span>
                )}
              </button>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {zoneStats.map((zone) => (
            <div key={zone.id} className="bg-[#050d18] rounded-xl p-4 border border-slate-700/30">
              <div className="flex items-center gap-2 mb-3">
                {zone.type === 'blind' ? (
                  <Blinds className="w-4 h-4 text-slate-400" />
                ) : (
                  <Square className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-base font-medium text-white truncate">{zone.name}</span>
              </div>
              
              {/* Position Slider */}
              <div className="mb-3">
                <div className="flex justify-between text-base text-slate-400 mb-1">
                  <span>Position</span>
                  <span>{zone.position}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={zone.position}
                  onChange={(e) => handleZonePositionChange(zone.id, parseInt(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${zone.position}%, #334155 ${zone.position}%, #334155 100%)`
                  }}
                />
              </div>
              
              {/* Status Label */}
              <div className={`text-center py-1.5 rounded-lg text-sm font-medium mb-3 ${
                zone.position === 0 ? 'bg-slate-700 text-slate-400' :
                zone.position === 100 ? 'bg-cyan-500/20 text-cyan-400' :
                'bg-slate-700/50 text-slate-300'
              }`}>
                {getPositionStatus(zone.position)}
              </div>
              
              {/* Mode and Auto Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-base text-slate-400">
                  {getModeIcon(zone.mode)}
                  <span>{getModeText(zone.mode)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Auto</span>
                  <button
                    onClick={() => handleZoneAutoToggle(zone.id)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${zone.isAuto ? 'bg-cyan-500' : 'bg-slate-600'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${zone.isAuto ? 'left-4' : 'left-0.5'}`} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
    </div>
  );
};

export default BlindCurtainControlView;

